import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FaceitLoginButton } from "@/components/layout";
import { Download, ExternalLink } from "lucide-react";
import { MapChip } from "@/components/cs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { date, mapName, plural } from "@/lib/format";
import { cn } from "@/lib/utils";

type FaceitMatch = {
  matchId: string; competition: string | null; finishedAt: number | null; map: string | null;
  result: "win" | "loss" | null; score: string | null; roomUrl: string; siteMatchId: string | null;
};
type Faceit =
  | { configured: false }
  | { configured: true; player: null; login: boolean; linked: boolean }
  | {
      configured: true; login: boolean; linked: boolean;
      player: { id: string; nickname: string; avatar: string | null; level: number | null; elo: number | null; url: string };
      matches: FaceitMatch[]; history?: { matches: number; loadingOlder: boolean };
    };

/** FACEIT skill level badge in the platform's colors. */
function Level({ level }: { level: number | null }) {
  if (!level) return null;
  const color = level >= 10 ? "#fe1f00" : level >= 8 ? "#ff6309" : level >= 4 ? "#ffc800" : level >= 2 ? "#1ce400" : "#eeeeee";
  return (
    <span className="grid size-8 place-items-center rounded-full border-2 font-display text-sm font-bold" style={{ borderColor: color, color }}>
      {level}
    </span>
  );
}

export function FaceitCard({ className }: { className?: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["faceit"], queryFn: () => api<Faceit>("/api/faceit"), staleTime: 60_000, retry: false });
  const unlink = useMutation({
    mutationFn: () => api("/auth/faceit/unlink", { method: "POST" }),
    onSuccess: () => (toast.success("FACEIT отвязан"), qc.invalidateQueries({ queryKey: ["faceit"] })),
    onError: (e) => toast.error(e.message),
  });
  const d = q.data;
  if (q.isLoading) return <Card className={cn("h-40 animate-pulse", className)} />;
  if (!d || !d.configured) return null;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display">
          <span className="rounded bg-[#ff5500] px-1.5 py-0.5 text-xs font-bold text-white">FACEIT</span> Матчи с FACEIT
        </CardTitle>
        {q.error ? (
          <CardDescription className="text-loss">{q.error.message}</CardDescription>
        ) : !d.player ? (
          <div className="space-y-3">
            <CardDescription>Аккаунт FACEIT с вашим Steam не найден. Привяжите его — войдите в FACEIT один раз.</CardDescription>
            {d.login && <FaceitLoginButton size="sm" label="Привязать FACEIT" />}
          </div>
        ) : (
          <CardDescription>
            Скачайте демку в комнате матча на FACEIT и перетащите её в загрузку — она попадёт в категорию FACEIT.
            Автозагрузка включится, когда FACEIT откроет нам доступ к скачиванию демок.
          </CardDescription>
        )}
      </CardHeader>
      {d.player && (
        <CardContent className="space-y-3">
          <a href={d.player.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg border bg-secondary/40 p-3 hover:bg-secondary">
            <Level level={d.player.level} />
            <div className="min-w-0 flex-1">
              <div className="truncate font-display font-semibold">{d.player.nickname}</div>
              <div className="num text-xs text-muted-foreground">{d.player.elo ? `${d.player.elo} ELO` : "без ELO"}</div>
            </div>
            <ExternalLink className="size-4 text-muted-foreground" />
          </a>
          {d.history && d.history.matches > 0 && (
            <p className="num text-xs text-muted-foreground">
              На сайте {d.history.matches} {plural(d.history.matches, ["матч", "матча", "матчей"])} с FACEIT
              {d.history.loadingOlder && " · в фоне догружаем более старые"}
            </p>
          )}
          {d.login && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {d.linked ? (
                <>
                  <span className="text-win">✓ привязан через вход FACEIT</span>
                  <button type="button" className="underline hover:text-foreground" onClick={() => unlink.mutate()}>Отвязать</button>
                </>
              ) : (
                <>
                  <span>Найден по вашему Steam.</span>
                  <FaceitLoginButton size="sm" label="Привязать и входить через FACEIT" />
                </>
              )}
            </div>
          )}
          {d.matches.length > 0 && (
            <div className="divide-y rounded-lg border">
              {d.matches.map((m) => (
                <div key={m.matchId} className="flex items-center gap-3 px-3 py-2">
                  <span className={cn("h-8 w-1 shrink-0 rounded-full", m.result === "win" ? "bg-win" : m.result === "loss" ? "bg-loss" : "bg-muted")} />
                  <MapChip map={m.map} className="size-9" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-semibold">{mapName(m.map)}</span>
                      {m.score && <span className="num text-sm">{m.score}</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{date(m.finishedAt)}{m.competition ? ` · ${m.competition}` : ""}</div>
                  </div>
                  {m.siteMatchId ? (
                    <Button asChild size="sm" variant="secondary"><Link to={`/match/${m.siteMatchId}`}>Открыть</Link></Button>
                  ) : (
                    <Button asChild size="sm" variant="ghost">
                      <a href={m.roomUrl} target="_blank" rel="noreferrer"><Download className="size-4" /> Демка</a>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
